import { useState } from "react";
import * as Sentry from "@sentry/react";
import {
  FirestoreError,
  FirestoreErrorCode,
  doc,
  getDoc
} from "firebase/firestore";
import { useSelector } from "react-redux";
import {
  IndividualState,
  UpdateIndividual
} from "../redux/individual/individualSlice";
import { individualSelectors } from "../redux/individual/selectors";
import { db } from "../api/firebaseApi";
import { IndividualsAPI } from "../api/individuals";
import { LawyerAPI } from "../api/lawyerApi";
import { DATABASE } from "../types/tables-data";

interface UpdateUserAvatar {
  avatarSrc: string;
  avatarName: string;
}

export const useUpdateUser = () => {
  const [isLoadingUpdateUser, setIsLoadingUpdateUser] = useState(false);
  const [errorUpdateUser, setErrorUpdateUser] =
    useState<FirestoreErrorCode | null>(null);
  const email = useSelector(individualSelectors.selectEmail);

  const onSubmitUpdateUser = async (id: string, values: UpdateIndividual) => {
    setIsLoadingUpdateUser(true);
    try {
      // Create an object with only the fields that are present in values
      const updateData: Record<string, any> = {};

      // Only add fields that are present in values
      Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined) {
          updateData[key] = value;
        }
      });

      // Add last_updated_by if email is available
      if (email) {
        updateData.last_updated_by = email;
      }

      await IndividualsAPI.updateIndividual(id, updateData);
    } catch (e) {
      if (e instanceof FirestoreError) {
        const errorCode = e.code;
        setErrorUpdateUser(errorCode);
      } else {
        setErrorUpdateUser("unknown");
      }
    } finally {
      setIsLoadingUpdateUser(false);
    }
  };

  const onUpdateUserAvatar = async (id: string, values: UpdateUserAvatar) => {
    setIsLoadingUpdateUser(true);
    try {
      await IndividualsAPI.updateIndividual(id, {
        avatarName: values.avatarName,
        avatarSrc: values.avatarSrc,
        last_updated_by: email
      });
    } catch (e) {
      if (e instanceof FirestoreError) {
        const errorCode = e.code;
        setErrorUpdateUser(errorCode);
      } else {
        setErrorUpdateUser("unknown");
      }
    } finally {
      setIsLoadingUpdateUser(false);
    }
  };

  const getUserBetaFeatures = async (
    userId: string,
    userType: DATABASE
  ): Promise<string[]> => {
    try {
      const userDocRef = doc(db, userType, userId);
      const snapshot = await getDoc(userDocRef);

      if (snapshot.exists()) {
        const data = snapshot.data();
        // If `betaFeatures` is not set, default to an empty array
        return data.betaFeatures ?? [];
      }

      // If `snapshot.exists()` is false, just return []
      return [];
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  /**
   * Updates the beta features array for a user in Firestore.
   * @param {string} userId - The user ID.
   * @param {string} userType
   * @param {string} feature - The beta feature to add or remove.
   * @param {boolean} hasAccess - Whether the user should have access to the feature.
   */
  const updateBetaFeatureAccess = async (
    userId: string,
    userType: DATABASE,
    feature: string,
    hasAccess: boolean
  ) => {
    try {
      // Both endpoints replace the whole betaFeatures list, so the new list
      // is computed client-side rather than with arrayUnion/arrayRemove.
      const currentFeatures = await getUserBetaFeatures(userId, userType);
      const newFeatures = hasAccess
        ? [...new Set([...currentFeatures, feature])]
        : currentFeatures.filter((f) => f !== feature);

      if (userType === DATABASE.INDIVIDUALS) {
        await IndividualsAPI.updateIndividual(userId, {
          betaFeatures: newFeatures
        });
      } else {
        await LawyerAPI.update(userId, { betaFeatures: newFeatures });
      }
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  return {
    getUserBetaFeatures,
    updateBetaFeatureAccess,
    isLoadingUpdateUser,
    onSubmitUpdateUser,
    errorUpdateUser,
    onUpdateUserAvatar
  };
};
